import React, { useCallback, useEffect, useState } from 'react';
import {
  CircleAlert, CircleCheckBig, Clock3, Eye, Fingerprint, Pencil, Play, Plus, Power, RefreshCw, Send, ServerCog, ShieldCheck, Trash2, Users, Wifi, WifiOff, X,
} from 'lucide-react';
import * as hr from '../api';
import Loader from '../components/Loader';
import { useHR } from '../HRContext';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { DateInput, Select } from '../components/Controls';
import { Chip, Kpi, iso } from './attendance/shared';
import { CMD_TONE, CONN_TONE, ROSTER_TONE, STATE_TONE, TIMEZONES, ago, clockText, dt, every } from './biometric/shared';

// Time & Attendance > Biometric Registrations > Devices: the ZKTeco devices that push punches to our ADMS receiver.
// A device calling in for the first time is listed as Pending Approval and refused (it keeps its punches) until approved.

export default function BiometricDevices() {
  const { notify } = useHR();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);   // {kind: new|edit|approve|view, row?}
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => hr.getDevices().then(r => { setD(r); setError(''); }).catch(e => setError(e.message)), []);
  useEffect(() => { load(); return every(15000, load); }, [load]);

  if (error && !d) return <div className="error">{error}</div>;
  if (!d) return <Loader title="Loading biometric devices…" subtitle="Fetching device health" />;
  const k = d.kpis;
  const pending = d.rows.filter(r => r.state === 'Pending Approval');

  const setState = async (r, state) => {
    try { notify((await hr.updateDevice(r.id, { state })).message); load(); } catch (e) { notify(e.message, 'bad'); }
  };
  const answer = ok => { const c = confirm; setConfirm(null); if (ok) c.go(); };
  const applyNow = async () => {
    try { notify((await hr.processPunches()).message); load(); } catch (e) { notify(e.message, 'bad'); }
  };

  return (
    <div className="mdr mds ael bio bdv">
      <div className="mdr-top">
        <div>
          <h2>Biometric Devices</h2>
          <p>ZKTeco devices that push punches to the receiver in real time: approval, health, device info and commands.</p>
        </div>
        <div className="mdr-top-actions no-print">
          <button className="mdr-btn" onClick={() => load().then(() => notify('Devices refreshed'))}><RefreshCw size={14} /> Refresh</button>
          {d.can.edit && <button className="mdr-btn" title="Punches are applied to attendance every minute; this does it now" onClick={applyNow}><Play size={14} /> Apply Punches Now{k.waiting ? ` (${k.waiting})` : ''}</button>}
          {d.can.create && <button className="mds-run" onClick={() => setModal({ kind: 'new' })}><Plus size={15} /> Add Device</button>}
        </div>
      </div>

      <div className="md-kpis mds-kpis bdv-kpis">
        <Kpi label="Devices" value={k.total} Ico={ServerCog} tone="blue" note="Registered or calling in" />
        <Kpi label="Online" value={k.online} Ico={Wifi} tone="green" note="Heard from in the last 2 min" i={1} />
        <Kpi label="Offline" value={k.offline} Ico={WifiOff} tone="red" note="Silent for over 2 min" i={2} />
        <Kpi label="Pending Approval" value={k.pending} Ico={CircleAlert} tone="amber" note="Refused until approved" i={3} />
        <Kpi label="Punches (24h)" value={k.punches_24h} Ico={Fingerprint} tone="purple" note="Received in the last day" i={4} />
        <Kpi label="Unmatched PINs" value={k.unmatched_pins} Ico={CircleAlert} tone="orange" note="Punches with no employee" i={5} />
        <Kpi label="Held for Review" value={k.held} Ico={Clock3} tone="rose" note="Doubtful device time: see Punch Exceptions" i={6} />
      </div>

      <div className="mdr-card bdv-setup">
        <div className="bdv-setup-head"><ShieldCheck size={16} /> <b>Connect a device</b></div>
        <ol>
          <li>On the device: <b>Menu › Comm › Cloud Server Setting</b>. Server address <code>{d.setup.host}</code>, port <code>{d.setup.port}</code>, HTTPS off.</li>
          <li>Within a minute it shows below as <b>Pending Approval</b> with its serial number and IP. Approve it and pick its site. Until then it keeps its punches and sends them once approved.</li>
          <li>Enrol staff on the device under the <b>Device PIN</b> on their biometric registration. A PIN that belongs to no one lands under Unmatched Punches.</li>
        </ol>
      </div>

      {pending.length > 0 && (
        <div className="mdr-card bdv-pending">
          <CircleAlert size={16} />
          <span><b>{pending.length} device{pending.length > 1 ? 's are' : ' is'} waiting for approval:</b> {pending.map(p => `${p.serial_no}${p.last_ip ? ` (${p.last_ip})` : ''}`).join(', ')}.
            Approve only serial numbers you recognise.</span>
        </div>
      )}

      <div className="mdr-card mdr-table-card">
        <div className="mdr-scroll">
          <table className="mdr-table mds-table ts-table bdv-table">
            <thead><tr><th className="c no-print">Action</th><th>Device</th><th>Serial No.</th><th>Site</th><th>Status</th><th>Connection</th><th>Last Seen</th>
              <th>Last Punch</th><th className="c">Punches 24h</th><th className="c">Users</th><th className="c">Fingerprints</th><th className="c">Faces</th><th>Clock</th><th>Firmware</th><th>IP</th></tr></thead>
            <tbody>
              {d.rows.map((r, i) => (
                <tr key={r.id} className="mdr-row" style={{ animationDelay: `${Math.min(i, 20) * 15}ms` }}>
                  <td className="c no-print nowrap">
                    <button className="mds-view" title="View Details" onClick={() => setModal({ kind: 'view', row: r })}><Eye size={12} /></button>
                    {d.can.edit && r.state === 'Pending Approval' && <button className="mds-view bdv-approve" title="Approve" onClick={() => setModal({ kind: 'approve', row: r })}><CircleCheckBig size={12} /></button>}
                    {d.can.edit && r.state !== 'Pending Approval' && <button className="mds-view ael-edit" title="Edit" onClick={() => setModal({ kind: 'edit', row: r })}><Pencil size={12} /></button>}
                    {d.can.edit && r.state === 'Active' && <button className="mds-view ael-no" title="Disable" onClick={() => setConfirm({ title: 'Disable device', danger: true, label: 'Disable',
                      message: `Disable ${r.name}?`, detail: 'Everything it sends will be refused until it is enabled again. It keeps its punches meanwhile.', go: () => setState(r, 'disabled') })}><Power size={12} /></button>}
                    {d.can.edit && r.state === 'Disabled' && <button className="mds-view bdv-approve" title="Enable" onClick={() => setState(r, 'active')}><Power size={12} /></button>}
                  </td>
                  <td className="nowrap"><b>{r.name}</b>{r.model && <small className="muted"> · {r.model}</small>}</td>
                  <td className="mono nowrap">{r.serial_no}</td>
                  <td className="nowrap">{r.site || <span className="muted">Not set</span>}{r.sync_group && <span className="bio-tag" title="Sync group">{r.sync_group}</span>}</td>
                  <td><Chip tone={STATE_TONE[r.state]}>{r.state}</Chip></td>
                  <td><Chip tone={CONN_TONE[r.connection]}>{r.connection}</Chip></td>
                  <td className="nowrap" title={dt(r.last_seen_at)}>{ago(r.last_seen_at)}</td>
                  <td className="nowrap" title={dt(r.last_punch_at)}>{ago(r.last_punch_at)}</td>
                  <td className="c">{r.punches_today}</td>
                  <td className="c">{r.user_count ?? '—'}</td><td className="c">{r.fp_count ?? '—'}</td><td className="c">{r.face_count ?? '—'}</td>
                  <td className="nowrap">{r.clock_offset_seconds == null ? <span className="muted">—</span>
                    : <span className={r.clock_warning ? 'bdv-clock-bad' : ''} title={`Estimated ${ago(r.clock_checked_at)}`}>{clockText(r.clock_offset_seconds)}</span>}</td>
                  <td className="nowrap">{r.firmware || '—'}</td>
                  <td className="mono nowrap">{r.last_ip || '—'}{r.ip_ok === false && <span className="bio-tag bdv-bad" title="Not in this device's allowed IPs">not allowed</span>}</td>
                </tr>
              ))}
              {!d.rows.length && <tr><td colSpan={15} className="mdr-empty">No devices yet. Point a device at {d.setup.host} and it will appear here, or add it by serial number.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="ael-pager"><span>{d.rows.length} device(s) · refreshes every 15 seconds</span></div>
      </div>

      {['new', 'edit', 'approve'].includes(modal?.kind) && <DeviceForm kind={modal.kind} r={modal.row} sites={d.sites} dialects={d.dialects} notify={notify}
        onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal?.kind === 'view' && <DeviceDetail id={modal.row.id} commands={d.commands} dialects={d.dialects} notify={notify} onClose={() => setModal(null)} onChanged={load} />}
      {confirm && <ConfirmDialog danger={confirm.danger} title={confirm.title} confirmLabel={confirm.label} message={confirm.message} detail={confirm.detail} onAnswer={answer} />}
    </div>
  );
}

function DeviceForm({ kind, r = {}, sites, dialects, notify, onClose, onSaved }) {
  const isNew = kind === 'new';
  const [f, setF] = useState({ serial_no: r.serial_no || '', name: r.name && r.name !== r.serial_no ? r.name : '', site_id: r.site_id ? String(r.site_id) : '',
    timezone: r.timezone || 'Africa/Lagos', allowed_ips: r.allowed_ips || '', remarks: r.remarks || '', log_heartbeats: r.log_heartbeats ?? true,
    handshake_dialect: r.handshake_dialect || 'text', sync_group: r.sync_group || '', trans_flag: r.trans_flag || '' });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const zones = TIMEZONES.includes(f.timezone) ? TIMEZONES : [f.timezone, ...TIMEZONES];
  const save = async () => {
    setBusy(true);
    const body = { name: f.name.trim() || null, site_id: f.site_id || null, timezone: f.timezone, allowed_ips: f.allowed_ips.trim() || null, remarks: f.remarks.trim() || null,
      handshake_dialect: f.handshake_dialect, sync_group: f.sync_group.trim() || null, trans_flag: f.trans_flag.trim() || null };
    try {
      const res = isNew ? await hr.createDevice({ ...body, serial_no: f.serial_no.trim() })
        : await hr.updateDevice(r.id, { ...body, log_heartbeats: f.log_heartbeats, ...(kind === 'approve' ? { state: 'active' } : {}) });
      notify(res.message); onSaved();
    } catch (e) { notify(e.message, 'bad'); } finally { setBusy(false); }
  };
  const title = { new: 'Add Biometric Device', edit: `Edit ${r.name}`, approve: `Approve Device ${r.serial_no}` }[kind];
  return (
    <Modal className="wh-modal" title={title} onClose={onClose}>
      <div className="mdr-form wh-form">
        {kind === 'approve' && <p className="muted small">First seen {dt(r.first_seen_at)} from {r.last_ip || 'an unknown IP'}. Approve only a serial number you recognise: once approved, the punches it sends are accepted.</p>}
        <div className="lvm-grid two">
          <label><span>Serial Number {isNew && <b className="wh-req soft">*</b>}</span>
            <input value={f.serial_no} readOnly={!isNew} className={isNew ? '' : 'bio-ro'} placeholder="As on the device: System › Device Info" onChange={e => set('serial_no', e.target.value.trim())} /></label>
          <label><span>Device Name</span><input value={f.name} placeholder="e.g. Lekki Site Main Gate" onChange={e => set('name', e.target.value)} /></label>
          <label><span>Project Site</span>
            <Select value={f.site_id} onChange={e => set('site_id', e.target.value)}><option value="">Not set</option>{sites.map(s => <option key={s.id} value={s.id}>{s.name}{s.state ? ` (${s.state})` : ''}</option>)}</Select></label>
          <label><span>Device Time Zone</span>
            <Select value={f.timezone} onChange={e => set('timezone', e.target.value)}>{zones.map(z => <option key={z} value={z}>{z}</option>)}</Select></label>
          <label className="bio-span"><span>Handshake Dialect</span>
            <Select value={f.handshake_dialect} onChange={e => set('handshake_dialect', e.target.value)}>{(dialects || []).map(x => <option key={x.key} value={x.key}>{x.label}</option>)}</Select>
            <small className="muted">Keep Standard unless the Device Log shows the device ignoring the handshake (it keeps asking, punches never arrive): older firmware wants Legacy.</small></label>
          <label><span>Sync Group</span><input value={f.sync_group} placeholder="Optional, e.g. LAGOS" onChange={e => set('sync_group', e.target.value.toUpperCase())} />
            <small className="muted">Devices sharing a group share their sites' employees and templates.</small></label>
          <label><span>TransFlag Override</span><input value={f.trans_flag} placeholder="Leave empty for the dialect's default" onChange={e => set('trans_flag', e.target.value)} />
            <small className="muted">What the device uploads. Only change it when the Device Log shows enrolments are not arriving.</small></label>
        </div>
        <label className="wh-block"><span>Allowed IPs</span>
          <input value={f.allowed_ips} placeholder="Optional. The site's public IP(s) or ranges, e.g. 102.89.10.4, 41.58.0.0/24" onChange={e => set('allowed_ips', e.target.value)} />
          <small className="muted">When set, requests carrying this serial number from any other IP are refused.</small></label>
        {!isNew && <label className="bdv-check"><input type="checkbox" checked={f.log_heartbeats} onChange={e => set('log_heartbeats', e.target.checked)} />
          Keep heartbeats in the Device Log <small className="muted">(useful while setting up; switch off once punches flow)</small></label>}
        <label className="wh-block"><span>Remarks</span><textarea rows={2} value={f.remarks} onChange={e => set('remarks', e.target.value)} /></label>
        <div className="mdh-form-foot wh-form-foot">
          <button className="mdr-btn" onClick={onClose}>Cancel</button>
          <button className="mds-run" disabled={busy || (isNew && !f.serial_no)} onClick={save}><CircleCheckBig size={14} /> {busy ? 'Saving…' : kind === 'approve' ? 'Approve Device' : isNew ? 'Add Device' : 'Save Changes'}</button>
        </div>
      </div>
    </Modal>
  );
}

function DeviceDetail({ id, commands, dialects, notify, onClose, onChanged }) {
  const [x, setX] = useState(null);
  const [cmd, setCmd] = useState({ kind: '', start: iso(new Date()), end: iso(new Date()) });
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const load = useCallback(() => hr.getDevice(id).then(setX).catch(e => notify(e.message, 'bad')), [id, notify]);
  useEffect(() => { load(); return every(10000, load); }, [load]);
  if (!x) return <Modal wide title="Device Details" onClose={onClose}><Loader title="Loading…" subtitle="Fetching the device" /></Modal>;
  const spec = commands.find(c => c.kind === cmd.kind);
  const active = x.state === 'Active';

  const queue = async () => {
    setBusy(true);
    try {
      const params = spec?.needs?.length ? { start: cmd.start, end: cmd.end } : {};
      notify((await hr.queueDeviceCommand(x.id, { kind: cmd.kind, params })).message);
      setCmd(c => ({ ...c, kind: '' })); load();
    } catch (e) { notify(e.message, 'bad'); } finally { setBusy(false); }
  };
  const cancel = async c => {
    try { notify((await hr.cancelDeviceCommand(c.id)).message); load(); } catch (e) { notify(e.message, 'bad'); }
  };
  const remove = async () => {
    try { notify((await hr.deleteDevice(x.id)).message); onChanged(); onClose(); } catch (e) { notify(e.message, 'bad'); }
  };
  const answer = ok => { const c = confirm; setConfirm(null); if (ok) (c === 'reboot' ? queue : remove)(); };
  const pushAll = async () => {
    setBusy(true);
    try { notify((await hr.pushDeviceUsers(x.id)).message); load(); } catch (e) { notify(e.message, 'bad'); } finally { setBusy(false); }
  };
  const ask = () => (cmd.kind === 'reboot' ? setConfirm('reboot') : queue());

  const groups = [
    ['Device', [['Name', x.name], ['Serial Number', x.serial_no], ['Site', x.site || 'Not set'], ['State', x.state_name || '—'],
      ['Status', <Chip key="s" tone={STATE_TONE[x.state]}>{x.state}</Chip>], ['Approved', x.approved_at ? `${dt(x.approved_at)}${x.approved_by ? ` by ${x.approved_by}` : ''}` : '—'],
      ['Time Zone', x.timezone], ['Allowed IPs', x.allowed_ips || 'Any'],
      ['Handshake', (dialects || []).find(v => v.key === x.handshake_dialect)?.label || x.handshake_dialect], ['TransFlag', x.trans_flag || 'Dialect default'],
      ['Sync Group', x.sync_group || 'None (own site)'], ['Remarks', x.remarks || '—']]],
    ['Health', [['Connection', <Chip key="c" tone={CONN_TONE[x.connection]}>{x.connection}</Chip>], ['Last Seen', `${ago(x.last_seen_at)} (${dt(x.last_seen_at)})`],
      ['Last Handshake', dt(x.last_handshake_at)], ['Last Punch', dt(x.last_punch_at)], ['First Seen', dt(x.first_seen_at)], ['Last IP', x.last_ip || '—'],
      ['Punches (24h)', String(x.punches_today)], ['Punches (all time)', String(x.total_punches)],
      ['Device Clock', x.clock_offset_seconds == null ? 'Not estimated yet' : `${clockText(x.clock_offset_seconds)} (estimated ${ago(x.clock_checked_at)})`]]],
    ['Reported by the Device', [['Firmware', x.firmware || '—'], ['Push Version', x.push_version || '—'], ['Users', x.user_count ?? '—'], ['Fingerprints', x.fp_count ?? '—'],
      ['Faces', x.face_count ?? '—'], ['Records', x.record_count ?? '—'], ['Device IP (LAN)', x.info?.device_ip || '—'], ['Reported', dt(x.info?.reported_at)]]],
  ];
  return (
    <Modal wide title={`Device — ${x.name}`} onClose={onClose}>
      <div className="ael-detail">
        {groups.map(([title, items]) => (
          <section key={title}><h4 className="wh-sub">{title}</h4>
            <div className="ael-kv">{items.map(([k, v]) => <div key={k} className={k === 'Remarks' ? 'wide' : ''}><small>{k}</small><span>{v ?? '—'}</span></div>)}</div></section>
        ))}

        {x.can.command && (
          <section><h4 className="wh-sub">Send a Command</h4>
            {!active ? <p className="muted small">Commands can be sent once the device is approved and active.</p> : (
              <div className="bdv-cmd">
                <Select value={cmd.kind} onChange={e => setCmd(c => ({ ...c, kind: e.target.value }))}>
                  <option value="">Choose a command…</option>{commands.map(c => <option key={c.kind} value={c.kind}>{c.label}</option>)}
                </Select>
                {spec?.needs?.length > 0 && <>
                  <label>From<DateInput value={cmd.start} onChange={e => setCmd(c => ({ ...c, start: e.target.value }))} /></label>
                  <label>To<DateInput value={cmd.end} onChange={e => setCmd(c => ({ ...c, end: e.target.value }))} /></label>
                </>}
                <button className="mds-run" disabled={!cmd.kind || busy} onClick={ask}><Send size={13} /> {busy ? 'Queuing…' : 'Queue Command'}</button>
              </div>
            )}
            <p className="muted small">The device collects queued commands on its next heartbeat (every few seconds while online). Command syntax is unconfirmed until tested on the real device: check the result below.</p>
          </section>
        )}

        <section><h4 className="wh-sub">Employees on this Device</h4>
          <div className="bdv-roster-head">
            <span>{Object.entries(x.roster_counts).filter(([, n]) => n).map(([k, n]) => `${n} ${k.toLowerCase()}`).join(' · ') || 'None sent yet'}
              {Object.keys(x.template_counts || {}).length > 0 && <> · templates: {Object.entries(x.template_counts).map(([k, n]) => `${n} ${k.toLowerCase()}`).join(', ')}</>}</span>
            {x.can.command && active && <button className="mdr-btn" disabled={busy} onClick={pushAll}><Users size={13} /> Send Employees</button>}
          </div>
          {x.roster.length > 0 && (
            <div className="mdr-scroll bdv-roster"><table className="mdr-table mds-table bdv-mini">
              <thead><tr><th>PIN</th><th>Employee</th><th>Last Sent</th><th>State</th><th>Confirmed</th><th>Reported by Device</th></tr></thead>
              <tbody>{x.roster.map(u => (
                <tr key={u.pin}><td className="mono">{u.pin}</td>
                  <td>{u.device_only ? <Chip tone="orange">On device only</Chip> : u.name}{u.employee_no && <small className="muted"> · {u.employee_no}</small>}</td>
                  <td>{u.device_only ? '—' : u.action}</td>
                  <td><Chip tone={ROSTER_TONE[u.state]}>{u.state}</Chip>{u.error && <small className="muted"> {u.error}</small>}</td><td className="nowrap"><small>{dt(u.synced_at)}</small></td><td className="nowrap"><small>{u.seen_on_device_at ? dt(u.seen_on_device_at) : '—'}</small></td></tr>
              ))}</tbody>
            </table></div>
          )}
          <p className="muted small">The app decides who is on each device: a registration with a Device PIN goes to the devices of its site and its sync group (every device when its site has none), with the employee's fingerprint and face templates, and is removed when it is made inactive or deleted. "On device only" is a user enrolled directly on the device under a PIN no registration has: give that PIN to the employee, or remove them on the device. Use "Upload its user list" below to check the device against this list.</p>
        </section>

        <section><h4 className="wh-sub">Command Log</h4>
          {x.command_log.length ? (
            <div className="mdr-scroll"><table className="mdr-table mds-table bdv-mini">
              <thead><tr><th>#</th><th>Command</th><th>Status</th><th>Queued</th><th>Result</th><th /></tr></thead>
              <tbody>{x.command_log.map(c => (
                <tr key={c.id}>
                  <td className="mono">{c.seq}</td>
                  <td><b>{c.kind}</b><br /><small className="mono muted">{c.command.replace(/\t/g, ' ')}</small></td>
                  <td><Chip tone={CMD_TONE[c.status]}>{c.status}</Chip>{c.attempts > 1 && <small className="muted"> ×{c.attempts}</small>}</td>
                  <td className="nowrap"><small>{dt(c.queued_at)}{c.queued_by ? ` · ${c.queued_by}` : ''}</small></td>
                  <td><small className="mono">{c.return_code != null ? `Return ${c.return_code}` : ''}{c.result && c.return_code == null ? c.result : ''}</small></td>
                  <td>{c.cancellable && x.can.command && <button className="mds-view ael-no" title="Cancel" onClick={() => cancel(c)}><X size={12} /></button>}</td>
                </tr>
              ))}</tbody>
            </table></div>
          ) : <p className="muted small">No commands sent yet.</p>}
        </section>

        {x.audit?.length > 0 && (
          <section><h4 className="wh-sub">Change History</h4>
            <ul className="ts-hist">{x.audit.map((a, i) => <li key={i}><b>{a.action}</b> · {a.user || 'System'} · {dt(a.at)}<br /><small>{a.summary}</small></li>)}</ul></section>
        )}
      </div>
      <div className="mdh-form-foot">
        {x.can.delete && x.total_punches === 0 && <button className="mdr-btn ael-danger-text" onClick={() => setConfirm('remove')}><Trash2 size={13} /> Remove Device</button>}
        <button className="mdr-btn" onClick={onClose}>Close</button>
      </div>
      {confirm && <ConfirmDialog danger title={confirm === 'reboot' ? 'Reboot device' : 'Remove device'} confirmLabel={confirm === 'reboot' ? 'Reboot' : 'Remove'}
        message={confirm === 'reboot' ? `Reboot ${x.name}?` : `Remove ${x.name} (${x.serial_no})?`}
        detail={confirm === 'reboot' ? 'Nobody can punch on it until it is back up, usually about a minute.' : 'If it calls in again it will be listed as pending.'}
        onAnswer={answer} />}
    </Modal>
  );
}
